import { useEffect, useRef, useState } from "react";
import type React from "react";
import { createPortal } from "react-dom";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatTime } from "@/utils/formatTime";
import { cn } from "@/lib/utils";

/** Fraction of the track the thumb must travel to exit */
const UNLOCK_THRESHOLD = 0.85;
const TRACK_PADDING_PX = 4;

interface PocketModeOverlayProps {
  recordingDuration: number;
  isSilenceWarningVisible: boolean;
  onExit: () => void;
}

interface DragState {
  pointerId: number;
  startX: number;
  maxOffset: number;
  direction: 1 | -1;
}

/**
 * Full-screen black overlay for recording with the phone put down.
 * Locking the phone stops the mic, so this keeps the screen on (wake lock)
 * while saving battery (black pixels on OLED) and swallowing accidental touches.
 * Only a deliberate swipe on the track exits.
 */
const PocketModeOverlay = ({
  recordingDuration,
  isSilenceWarningVisible,
  onExit,
}: PocketModeOverlayProps) => {
  const { t } = useLanguage();
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const progressRef = useRef(0);
  const [thumbOffset, setThumbOffset] = useState(0);
  const [isRtl, setIsRtl] = useState(false);

  useEffect(() => {
    if (trackRef.current) setIsRtl(getComputedStyle(trackRef.current).direction === "rtl");
  }, []);

  // Fullscreen hides the browser/system bars (Android); unsupported on iPhone
  useEffect(() => {
    void document.documentElement.requestFullscreen?.({ navigationUI: "hide" })?.catch(() => {});
    return () => {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    };
  }, []);

  const resetThumb = () => {
    progressRef.current = 0;
    setThumbOffset(0);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!track || !thumb) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      maxOffset: track.clientWidth - thumb.offsetWidth - TRACK_PADDING_PX * 2,
      direction: isRtl ? -1 : 1,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const distance = Math.min(Math.max((event.clientX - drag.startX) * drag.direction, 0), drag.maxOffset);
    progressRef.current = drag.maxOffset > 0 ? distance / drag.maxOffset : 0;
    setThumbOffset(distance * drag.direction);
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (progressRef.current >= UNLOCK_THRESHOLD) {
      onExit();
      return;
    }
    resetThumb();
  };

  const SwipeIcon = isRtl ? ChevronsLeft : ChevronsRight;

  // Portal to body so the overlay sits above the fixed app header (sidebar trigger, notifications)
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("pocketMode.title")}
      className="fixed inset-0 z-[1100] flex touch-none select-none flex-col items-center justify-between bg-black p-8"
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="flex flex-col items-center gap-4 pt-16 text-muted-foreground opacity-60">
        <div className="flex items-center gap-2">
          <span className="size-2 animate-pulse rounded-full bg-red-500" aria-hidden />
          <span className="tabular-nums text-2xl font-semibold">{formatTime(recordingDuration)}</span>
        </div>
        <p className="text-sm">{t("pocketMode.recording")}</p>
        {isSilenceWarningVisible && (
          <p role="alert" className="text-center text-sm text-red-500">
            {t("recordingSilence.title")}
          </p>
        )}
      </div>

      <div className="flex w-full max-w-sm flex-col items-center gap-4 pb-8">
        <p className="text-center text-xs text-muted-foreground opacity-50">{t("pocketMode.hint")}</p>
        <div
          ref={trackRef}
          className="relative h-14 w-full rounded-full border border-muted-foreground/30 p-1"
        >
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground opacity-50">
            {t("pocketMode.unlock")}
          </span>
          <div
            ref={thumbRef}
            role="slider"
            aria-label={t("pocketMode.unlock")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressRef.current * 100)}
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onKeyDown={(event) => {
              if (event.key === "Enter") onExit();
            }}
            className={cn(
              "relative flex size-12 cursor-grab items-center justify-center rounded-full bg-muted-foreground/40 text-background",
              !dragRef.current && "transition-transform"
            )}
            style={{ transform: `translateX(${thumbOffset}px)` }}
          >
            <SwipeIcon className="size-5" aria-hidden />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PocketModeOverlay;
